/**
 * Filter toggle property getter and registration.
 *
 * Storage key: identifier=`filter.{annotationId}`, objectIdentifier=`toggles`, type=PROJECT_USER
 *
 * The static config uses `dynamicIdentifier` so PropertyPanel resolves
 * the identifier per-annotation and `objectIdentifierOverride` so it reads
 * from the correct objectIdentifier.
 */

import { AnnotationType, PropertyType, PropertyValueType } from '../../types';
import { propertyRegistry } from '../../PropertyRegistry';
import { ReactiveProperty } from '../../reactive/ReactiveProperty';
import { getGlobalPropertyService, hasGlobalPropertyService } from '../../reactive/globalPropertyService';

const TOGGLE_OBJECT_ID = 'toggles';

/** Annotation types that support filtering */
const FILTERABLE_TYPES = [AnnotationType.BOX, AnnotationType.POINTCLUSTER, AnnotationType.SURFACE];

/**
 * Register the static filter toggle config for PropertyPanel auto-rendering.
 */
function registerFilterToggleConfig(): void {
  if (propertyRegistry.isRegistered('filter.{objectIdentifier}')) return;
  propertyRegistry.register({
    type: PropertyType.PROJECT_USER,
    valueType: PropertyValueType.BOOLEAN,
    identifier: 'filter.{objectIdentifier}',
    name: 'Toggle filter',
    annotationTypes: FILTERABLE_TYPES,
    defaultValue: false,
    dynamicIdentifier: true,
    objectIdentifierOverride: TOGGLE_OBJECT_ID,
    ui: {
      group: 'filters',
      order: 0,
      isGroupToggle: true,
    },
  });
}

// Auto-register on import
registerFilterToggleConfig();

/**
 * Ensure the per-instance reactive property exists in the PropertyService.
 * Called by Potree-side code (Volume, Plane, PointCluster) to subscribe.
 */
export function ensureFilterTogglePropertyRegistered(annotationIdentifier: string): string {
  const propertyIdentifier = `filter.${annotationIdentifier}`;

  if (!propertyRegistry.isRegistered(propertyIdentifier)) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_USER,
      valueType: PropertyValueType.BOOLEAN,
      identifier: propertyIdentifier,
      name: `filter for ${annotationIdentifier}`,
      annotationTypes: [],
      defaultValue: false,
    });
  }

  return propertyIdentifier;
}

/**
 * Get reactive property for a filter toggle (non-hook version for Potree).
 * Returns null if the global property service is not yet initialized.
 */
export function getFilterToggleProperty(annotationIdentifier: string): ReactiveProperty<boolean> | null {
  if (!hasGlobalPropertyService()) return null;

  const propertyIdentifier = ensureFilterTogglePropertyRegistered(annotationIdentifier);
  const service = getGlobalPropertyService();
  return service.getProperty(propertyIdentifier, TOGGLE_OBJECT_ID, PropertyType.PROJECT_USER) as ReactiveProperty<boolean>;
}
